/**
 * <PricingConfigEditor> — admin editor for the singleton pricing_config
 * `base_ratio_subscription_to_cogs` value.
 *
 * signup-billing-mvp Phase 5 Session 6 (2026-05-13).
 *
 * 4-state pattern (loading / error / empty / success). The "empty" state
 * doesn't really apply here — the singleton row is always seeded by the
 * Phase-2 migration — so the success path handles the row presence directly.
 *
 * Live derived-grants preview: as admin types, recomputes
 *   derived_grant = base_tier.monthly_price_usd × tier.usage_multiplier × new_ratio
 * and shows OLD vs NEW columns per active tier.
 *
 * On Submit: gated by <AdminConfirmDialog> (type CONFIRM to submit). PUT
 * payload includes `version` for optimistic concurrency. 409 surfaces the
 * error AND refreshes from `details.current_row` so the next attempt uses
 * the fresh version.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, EmptyState, Skeleton } from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { AdminConfirmDialog } from './AdminConfirmDialog';

interface DerivedGrant {
  tier_id: string;
  monthly_grant_cogs: number;
}

interface PricingConfigData {
  base_ratio_subscription_to_cogs: number;
  version: number;
  updated_at: string;
  updated_by_email?: string | null;
  derived_grants: DerivedGrant[];
}

interface TierRow {
  id: string;
  usage_multiplier: number;
  monthly_price_usd: number | null;
}

interface Envelope<T> {
  data: T;
}

const RATIO_MIN = 1.0;
const RATIO_MAX = 500.0;

export function PricingConfigEditor() {
  const queryClient = useQueryClient();
  const [draftRatio, setDraftRatio] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showHistoryDrawer, setShowHistoryDrawer] = useState(false);

  const configQuery = useQuery<PricingConfigData>({
    queryKey: queryKeys.billing.pricingConfig(),
    queryFn: async () => {
      const env = await api.get<Envelope<PricingConfigData>>(
        '/api/billing/admin/pricing-config',
      );
      return env.data;
    },
    staleTime: 30_000,
  });

  const tiersQuery = useQuery<TierRow[]>({
    queryKey: queryKeys.billing.adminTiers(),
    queryFn: async () => {
      const env = await api.get<Envelope<TierRow[]>>('/api/billing/admin/tiers');
      return env.data;
    },
    staleTime: 30_000,
  });

  // Seed the draft once the config loads.
  useMemo(() => {
    if (configQuery.data && draftRatio === '') {
      setDraftRatio(String(configQuery.data.base_ratio_subscription_to_cogs));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configQuery.data]);

  const parsedDraft = parseFloat(draftRatio);
  const draftValid =
    Number.isFinite(parsedDraft) && parsedDraft >= RATIO_MIN && parsedDraft <= RATIO_MAX;

  // Per-tier preview rows — re-locked v2.12.5 (2026-05-14) to show
  // $ Anthropic allowance instead of COGS counts. Rationale: per the
  // root-CLAUDE.md COGS-vs-dollars footgun, `monthly_grant_cogs = price ×
  // multiplier` (NO `× base_ratio`), so the COGS grant is INVARIANT to
  // base_ratio changes. The meaningful business signal of a ratio change
  // is "$ of Anthropic each tier's COGS grant unlocks" = `grant_cogs /
  // base_ratio`. Showing that lets the admin see "this tightens Principal
  // from $24.95 → $19.96 Anthropic per month" rather than a no-op COGS
  // delta.
  const previewRows = useMemo(() => {
    if (!draftValid || !configQuery.data || !tiersQuery.data) return [];
    const baseTier = tiersQuery.data.find((t) => t.usage_multiplier === 1);
    const basePrice = baseTier?.monthly_price_usd ?? 0;
    const oldRatio = configQuery.data.base_ratio_subscription_to_cogs;
    return tiersQuery.data.map((t) => {
      const grantCogs = Math.round(basePrice * t.usage_multiplier);
      const oldAnthropicUsd = oldRatio > 0 ? grantCogs / oldRatio : 0;
      const newAnthropicUsd = parsedDraft > 0 ? grantCogs / parsedDraft : 0;
      return {
        tier_id: t.id,
        grant_cogs: grantCogs,
        old_anthropic_usd: oldAnthropicUsd,
        new_anthropic_usd: newAnthropicUsd,
        delta_anthropic_usd: newAnthropicUsd - oldAnthropicUsd,
      };
    });
  }, [draftValid, parsedDraft, configQuery.data, tiersQuery.data]);

  const updateMutation = useMutation({
    mutationFn: async (input: {
      base_ratio_subscription_to_cogs: number;
      version: number;
    }) => {
      const env = await api.put<Envelope<PricingConfigData>>(
        '/api/billing/admin/pricing-config',
        input,
      );
      return env.data;
    },
    onSuccess: (data) => {
      // Update the local cache + reseed draft to the new ratio.
      queryClient.setQueryData(queryKeys.billing.pricingConfig(), data);
      queryClient.invalidateQueries({
        queryKey: queryKeys.billing.adminTiers(),
      });
      setDraftRatio(String(data.base_ratio_subscription_to_cogs));
      setSubmitError(null);
      setDialogOpen(false);
    },
    onError: (err: unknown) => {
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        err.code === 'OPTIMISTIC_LOCK_FAILED'
      ) {
        const currentRow = err.details?.['current_row'] as
          | { base_ratio_subscription_to_cogs: number; version: number }
          | null
          | undefined;
        if (currentRow) {
          // Refresh local cache from server's current row (partial — full re-fetch via invalidate).
          queryClient.invalidateQueries({
            queryKey: queryKeys.billing.pricingConfig(),
          });
          setSubmitError(
            `Pricing config was edited by another admin (now version ${currentRow.version}). The form has been refreshed — review and re-submit.`,
          );
          setDraftRatio(String(currentRow.base_ratio_subscription_to_cogs));
        } else {
          setSubmitError('Pricing config was edited by another admin. Please refresh.');
        }
      } else if (err instanceof ApiError) {
        setSubmitError(`${err.code ?? 'ERROR'}: ${err.message}`);
      } else if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError('Unknown error');
      }
      setDialogOpen(false);
    },
  });

  function handleOpenConfirm() {
    setSubmitError(null);
    if (!draftValid || !configQuery.data) return;
    if (parsedDraft === configQuery.data.base_ratio_subscription_to_cogs) {
      setSubmitError('New ratio matches current — nothing to submit.');
      return;
    }
    setDialogOpen(true);
  }

  function handleConfirm() {
    if (!configQuery.data || !draftValid) return;
    updateMutation.mutate({
      base_ratio_subscription_to_cogs: parsedDraft,
      version: configQuery.data.version,
    });
  }

  return (
    <div className="flex flex-col gap-3" data-testid="pricing-config-editor">
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono font-semibold text-text-muted tracking-[1.4px] uppercase">
          Pricing Config
        </span>
        {configQuery.data && (
          <button
            type="button"
            onClick={() => setShowHistoryDrawer((v) => !v)}
            className="text-xs font-mono text-text-muted hover:text-text-bright cursor-pointer underline"
            data-testid="pricing-config-history-toggle"
          >
            {showHistoryDrawer ? 'Hide history' : 'Show history'}
          </button>
        )}
      </div>

      {/* Loading state */}
      {configQuery.isLoading && (
        <div className="flex flex-col gap-2" data-testid="pricing-config-loading">
          <Skeleton variant="line" className="h-10 w-full" />
          <Skeleton variant="line" className="h-32 w-full" />
        </div>
      )}

      {/* Error state */}
      {configQuery.isError && !configQuery.isLoading && (
        <div
          className="px-3 py-2 bg-danger/[0.06] border border-danger/15 rounded text-danger text-xs font-mono"
          data-testid="pricing-config-error"
        >
          Failed to load pricing config.{' '}
          <button
            className="underline cursor-pointer"
            onClick={() => configQuery.refetch()}
            type="button"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty (singleton-missing — defensive only; migration seeds it) */}
      {configQuery.isSuccess && !configQuery.data && (
        <EmptyState
          title="PRICING CONFIG NOT SEEDED"
          description="The Phase-2 migration seeds the singleton row; if you're seeing this, contact engineering."
        />
      )}

      {/* Success state */}
      {configQuery.isSuccess && configQuery.data && (
        <div className="flex flex-col gap-3">
          {/* Editor row */}
          <div className="flex items-end gap-3 px-3 py-3 bg-panel-bg/40 border border-panel-border rounded-[3px]">
            <label className="flex flex-col gap-1 flex-1">
              <span className="text-xs font-mono text-text-muted tracking-[1.1px] uppercase">
                Base ratio (COGS per $1 of Anthropic spend)
              </span>
              <input
                type="number"
                min={RATIO_MIN}
                max={RATIO_MAX}
                step={0.1}
                value={draftRatio}
                onChange={(e) => setDraftRatio(e.target.value)}
                className="px-3 py-2 bg-input-bg border border-input-border rounded-[2px] text-text-bright text-sm font-mono outline-none focus:border-accent/20"
                data-testid="pricing-config-ratio-input"
              />
              <span className="text-micro font-mono text-text-dim">
                Bounds: {RATIO_MIN}–{RATIO_MAX}. Current: {configQuery.data.base_ratio_subscription_to_cogs}× ·
                version {configQuery.data.version}
              </span>
            </label>
            <Button
              variant="primary"
              disabled={!draftValid || updateMutation.isPending}
              onClick={handleOpenConfirm}
              data-testid="pricing-config-update-btn"
            >
              {updateMutation.isPending ? 'Saving…' : 'Update'}
            </Button>
          </div>

          {/* Live derived-allowance preview — shows $ of Anthropic each
              tier's COGS grant unlocks before vs after the ratio change.
              COGS grant itself is invariant to base_ratio. */}
          {draftValid && previewRows.length > 0 && (
            <div
              className="overflow-x-auto border border-panel-border rounded-[3px]"
              data-testid="pricing-config-derived-preview"
            >
              <div className="px-3 py-2 text-micro font-mono uppercase tracking-[1.1px] text-text-dim border-b border-panel-border bg-panel-bg/40">
                $ Anthropic allowance per tier · grant_cogs ÷ base_ratio
              </div>
              <table className="w-full text-xs font-mono">
                <thead>
                  <tr className="border-b border-panel-border bg-panel-bg/40">
                    <th className="text-left px-3 py-2 text-text-muted font-semibold tracking-[1.2px] uppercase">
                      Tier
                    </th>
                    <th className="text-right px-3 py-2 text-text-muted font-semibold tracking-[1.2px] uppercase">
                      Grant (COGS)
                    </th>
                    <th className="text-right px-3 py-2 text-text-muted font-semibold tracking-[1.2px] uppercase">
                      $ at OLD {configQuery.data.base_ratio_subscription_to_cogs}×
                    </th>
                    <th className="text-right px-3 py-2 text-text-muted font-semibold tracking-[1.2px] uppercase">
                      $ at NEW {parsedDraft}×
                    </th>
                    <th className="text-right px-3 py-2 text-text-muted font-semibold tracking-[1.2px] uppercase">
                      Δ $
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row) => {
                    const delta = row.delta_anthropic_usd;
                    const fmt$ = (v: number): string =>
                      `$${v.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`;
                    return (
                      <tr
                        key={row.tier_id}
                        className="border-b border-panel-border/40"
                        data-testid={`pricing-preview-row-${row.tier_id}`}
                      >
                        <td className="px-3 py-2 text-text-bright">{row.tier_id}</td>
                        <td className="px-3 py-2 text-right text-text-muted tabular-nums">
                          {row.grant_cogs.toLocaleString('en-US')}
                        </td>
                        <td className="px-3 py-2 text-right text-text-muted tabular-nums">
                          {fmt$(row.old_anthropic_usd)}
                        </td>
                        <td className="px-3 py-2 text-right text-accent tabular-nums">
                          {fmt$(row.new_anthropic_usd)}
                        </td>
                        <td
                          className={`px-3 py-2 text-right tabular-nums ${
                            Math.abs(delta) < 0.005
                              ? 'text-text-dim'
                              : delta > 0
                                ? 'text-accent'
                                : 'text-warn'
                          }`}
                        >
                          {Math.abs(delta) < 0.005
                            ? '—'
                            : `${delta > 0 ? '+' : ''}${fmt$(delta).replace('$', delta < 0 ? '-$' : '$').replace('$-$', '-$')}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="px-3 py-2 text-micro font-mono text-text-dim border-t border-panel-border bg-panel-bg/40">
                COGS grant is invariant to base_ratio — only the $ Anthropic
                value of that grant changes. Per-event metering uses the
                ratio at debit time.
              </div>
            </div>
          )}

          {submitError && (
            <div
              className="px-3 py-2 bg-danger/[0.06] border border-danger/15 rounded text-danger text-xs font-mono"
              data-testid="pricing-config-submit-error"
            >
              {submitError}
            </div>
          )}

          {/* Change history drawer (read-only — server returns updated_by_email + updated_at) */}
          {showHistoryDrawer && (
            <div
              className="px-3 py-2 bg-input-bg border border-panel-border rounded-[2px] text-xs font-mono text-text-muted"
              data-testid="pricing-config-history"
            >
              <span className="block">
                Last update: {configQuery.data.updated_at}
              </span>
              <span className="block">
                Updated by: {configQuery.data.updated_by_email ?? 'unknown'}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Type-CONFIRM gate */}
      {configQuery.data && (
        <AdminConfirmDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onConfirm={handleConfirm}
          title="Update Pricing Config"
          confirmLabel="Apply ratio change"
          dangerColor
          body={
            <span>
              You are about to change the base COGS-per-dollar ratio. This affects EVERY
              active subscription's monthly grant at the next hourly cron tick (unless the
              subscriber has an admin-pinned grant override). Are you sure?
            </span>
          }
          summary={
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span>OLD ratio</span>
                <span className="text-text-bright">
                  {configQuery.data.base_ratio_subscription_to_cogs}×
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>NEW ratio</span>
                <span className="text-accent">{parsedDraft}×</span>
              </div>
              <div className="text-text-dim text-micro tracking-[1.1px] uppercase pt-1">
                Affects {previewRows.length} active tier
                {previewRows.length === 1 ? '' : 's'} — see $ allowance preview above.
              </div>
            </div>
          }
        />
      )}
    </div>
  );
}
